import ConferencesResource from 'flow/api/conferences';

/**
 * POST /conferences/meetingcode/{code}/participant-identity — VID-264
 * (ConferencesResource#registerParticipantIdentity). Binds the caller's join
 * UUID to their authenticated identity so the roster can classify participants
 * as external/colleague and expose viewerIsMeetingAdmin.
 *
 * The response carries the caller's MeetingRole, which this deliberately drops:
 * by the time the UUID exists the join is already under way, so the role
 * arrives too late to decide anything. getMyMeetingRole below is the one to
 * ask before joining.
 */
export const registerParticipantIdentity = (
    meetingCode: string,
    uuid: string,
): Promise<void> =>
    new Promise((resolve, reject) => {
        ConferencesResource.registerParticipantIdentity(
            {
                onResult: () => resolve(),
                onError: reject,
                onClientError: reject,
            },
            meetingCode,
            { uuid },
        );
    });

interface MeetingRoleResponse {
    meetingRole?: string;
}

/**
 * How long to wait for an answer before treating its absence as the answer.
 * HomeClient neither times out nor aborts, so a request that is never answered
 * — a held socket, a proxy that keeps the connection and returns nothing — would
 * otherwise leave this promise pending for the life of the page. The lobby shows
 * a skeleton in place of the join button while it waits, so an unbounded wait is
 * not a slow button but no button at all, and no way into the meeting.
 */
const ROLE_ANSWER_TIMEOUT_MS = 4000;

/**
 * GET /conferences/meetingcode/{code}/my-meeting-role — VID-76
 * (ConferencesResource#getMyMeetingRole).
 *
 * How home classifies the caller for this meeting, keyed on the meeting code
 * alone. The sibling POST above answers the same question but is keyed on the
 * join UUID, which does not exist until the join has already started — too late
 * to label the button that starts it. Read-only; captures nothing.
 *
 * Authenticated: a guest cannot call this, and is external by definition.
 * Resolves to null rather than rejecting when there is no answer, which reads
 * the same as a guest's silence and lands on the same conclusion — ask to join.
 */
export const getMyMeetingRole = (meetingCode: string): Promise<string | null> =>
    new Promise((resolve) => {
        const settle = (role: string | null) => {
            clearTimeout(giveUp);
            resolve(role);
        };
        const giveUp = setTimeout(() => resolve(null), ROLE_ANSWER_TIMEOUT_MS);

        ConferencesResource.getMyMeetingRole(
            {
                onResult: (response: MeetingRoleResponse) =>
                    settle(response?.meetingRole ?? null),
                onError: () => settle(null),
                onClientError: () => settle(null),
            },
            meetingCode,
        );
    });
